<template>
  <eg-card
    data-testid="grading-details-form"
    :label="$t('rubrics.customizeGrading')"
    onboarding-key="onboarding:clicked:submissionFormGradingDetailsQuestionIcon"
  >
    <div class="paragraph-s text-neutral-secondary mb-4 mt-2">
      {{ $t("rubrics.fineTuneGrading") }}
    </div>

    <div class="eg-card-item">
      <div class="d-flex flex-column mb-3">
        <div class="paragraph-m semi-bold mb-2">
          {{ $t("rubrics.studentLevel") }}
        </div>
        <div class="paragraph-s text-neutral-secondary">
          {{ $t("rubrics.studentLevelDescription") }}
        </div>
      </div>

      <rubric-grade-level-radio-group
        :value="level"
        horizontal
        :required="true"
        @input="handleLevelChange"
      />
    </div>

    <div class="eg-card-item">
      <div class="d-flex flex-column mb-3">
        <div class="paragraph-m semi-bold mb-2">
          {{ $t("rubrics.language") }}
        </div>
        <div class="paragraph-s text-neutral-secondary">
          {{ $t("rubrics.languageDescription") }}
        </div>
      </div>

      <essay-language-select
        :value="inputLanguageId"
        @input="onInputLanguageChange"
      />
    </div>

    <div>
      <eg-b-form-checkbox
        :checked="sameOutputLanguage"
        class="w-100 mb-4"
        @input="onSameLanguageToggle"
      >
        <div class="paragraph-s text-neutral-secondary">
          {{ $t("rubrics.useSameLanguage") }}
        </div>
      </eg-b-form-checkbox>
    </div>

    <div v-if="!sameOutputLanguage" class="eg-card-item mb-4">
      <div class="d-flex flex-column mb-3">
        <div class="paragraph-m semi-bold mb-2">
          {{ $t("rubrics.feedbackLanguageLabel") }}
        </div>
        <div class="paragraph-s text-neutral-secondary">
          {{ $t("rubrics.feedbackLanguageDescription") }}
        </div>
      </div>

      <essay-language-select
        :value="outputLanguageId"
        :disabled-languages="disabledOutputLanguages"
        @input="updateField('outputLanguageId', $event)"
      />
    </div>

    <div>
      <div class="paragraph-m semi-bold mb-2">
        {{ $t("rubrics.feedbackDetailLevel") }}
      </div>

      <div class="paragraph-s text-neutral-secondary mb-3">
        {{ $t("rubrics.feedbackDetailLevelDescription") }}
      </div>

      <feedback-length :value="length" @input="updateField('length', $event)" />
    </div>

    <div class="eg-card-item">
      <div class="paragraph-m semi-bold mb-2">
        {{ $t("rubrics.gradingIntensityLabel") }}
      </div>

      <div class="paragraph-s text-neutral-secondary mb-3">
        {{ $t("rubrics.gradingIntensityDesc") }}
      </div>

      <feedback-intensity
        :value="intensity"
        @input="updateField('intensity', $event)"
      />
    </div>

    <!-- Advanced Rules Section -->
    <div class="eg-card-item">
      <eg-advanced-settings
        :config="advancedSettingsConfig"
        :collapsed.sync="advancedRulesOpen"
        :update-field="updateField"
      />
    </div>

    <div class="eg-card-item">
      <div class="paragraph-m semi-bold mb-2">
        {{ $t("rubrics.additionalInstructions") }}
        <span class="paragraph-s text-neutral-secondary">{{
          $t("rubrics.additionalInstructionsOptional")
        }}</span>
      </div>
      <eg-input
        :value="customInstructions"
        variant="textarea"
        :rows="4"
        :placeholder="customInstructionsPlaceholder"
        @input="updateField('customInstructions', $event)"
      />
    </div>
  </eg-card>
</template>

<script>
import EgInput from "@/components/global/eg/eg_input.vue";
import FeedbackLength from "@/components/global/form/feedback_length.vue";
import FeedbackIntensity from "@/components/global/form/feedback_intensity.vue";
import RubricGradeLevelRadioGroup from "@/components/global/form/rubric_grade_level_radio_group.vue";
import EgCard from "@/components/global/eg_card.vue";
import EssayLanguageSelect from "@/components/dashboard/components/essay_language_select.vue";
import EgAdvancedSettings from "@/components/global/eg/eg_advanced_settings.vue";
import {
  FEEDBACK_LENGTH,
  GRADE_LEVELS,
  INTENSITIES,
  MAP_GRADE_LEVELS_TO_FEEDBACK_LENGTH_INDEX,
} from "@/constants";

const FIELDS = [
  "level",
  "intensity",
  "length",
  "rubricId",
  "sourceMaterialId",
  "inputLanguageId",
  "outputLanguageId",
  "customInstructions",
  "flexibleScoring",
  // "citationMla",
  // "citationApa",
  // "citationChicago",
];

export default {
  name: "GradingDetailsForm",
  components: {
    EgCard,
    EssayLanguageSelect,
    RubricGradeLevelRadioGroup,
    EgInput,
    FeedbackLength,
    FeedbackIntensity,
    EgAdvancedSettings,
  },
  props: {
    rubric: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    sourceMaterial: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    value: {
      type: Object,
      default: () => ({}),
      validator: (value) => {
        const keys = Object.keys(value);
        FIELDS.forEach((key) => {
          if (!keys.includes(key)) {
            return false;
          }
        });

        return true;
      },
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      level: GRADE_LEVELS[1].value,
      intensity: INTENSITIES[1].value,
      length:
        FEEDBACK_LENGTH[
          MAP_GRADE_LEVELS_TO_FEEDBACK_LENGTH_INDEX[GRADE_LEVELS[1].value]
        ].value,
      rubricId: null,
      sourceMaterialId: null,
      inputLanguageId: null,
      outputLanguageId: null,
      customInstructions: "",
      flexibleScoring: false,
      // citationMla: false,
      // citationApa: false,
      // citationChicago: false,
      sameOutputLanguage: true,
      previousOutputLanguageId: null,
      disabledOutputLanguages: [],
      advancedRulesOpen: false,
    };
  },
  computed: {
    customInstructionsPlaceholder() {
      return this.$t("rubrics.instructionsPlaceholder");
    },
    isFlexibleScoringLocked() {
      return this.rubric?.isFlexibleScoringLocked;
    },
    flexibleScoringDisabledMessage() {
      if (!this.isFlexibleScoringLocked) {
        return "";
      }
      return this.flexibleScoring
        ? this.$t("flexibleScoring.descriptionOn")
        : this.$t("flexibleScoring.descriptionOff");
    },
    rubricTypeName() {
      return this.rubric?.name || "";
    },
    advancedSettingsConfig() {
      return {
        title: this.$t("rubrics.advanced"),
        groups: [
          {
            id: "flexible-scoring",
            title: this.$t("flexibleScoring.title"),
            onboardingKey:
              "onboarding:clicked:submissionFormFlexibleScoringQuestionIcon",
            dataTestId: "grading-details-flexible-scoring",
            items: [
              {
                field: "flexibleScoring",
                value: this.flexibleScoring,
                label: this.flexibleScoring
                  ? this.$t("flexibleScoring.descriptionOn")
                  : this.$t("flexibleScoring.descriptionOff"),
                disabled: this.isFlexibleScoringLocked,
                disabledMessage: this.flexibleScoringDisabledMessage,
              },
            ],
          },
          //TODO: wait for API and final approve
          /*{
            id: "citation-styles",
            title: "Citation Styles",
            onboardingKey:
              "onboarding:clicked:submissionFormCitationStylesQuestionIcon",
            items: [
              {
                field: "citationMla",
                value: this.citationMla,
                label: "MLA (Modern Language Association style)",
              },
              {
                field: "citationApa",
                value: this.citationApa,
                label: "APA (American Psychological Association style)",
              },
              {
                field: "citationChicago",
                value: this.citationChicago,
                label: "Chicago / Turabian",
              },
            ],
          },*/
        ],
      };
    },
  },
  watch: {
    rubric(value) {
      this.setRubric(value);
    },
    sourceMaterial(value) {
      this.sourceMaterialId = value?.id || null;
    },
    value: {
      handler: "initFields",
      deep: true,
    },
  },
  created() {
    this._selfUpdate = false;
    this.initFields();
    if (this.rubric && !this.isEdit) {
      this.setRubric(this.rubric);
    }
  },
  methods: {
    initFields() {
      const skipSync = this._selfUpdate;
      this._selfUpdate = false;

      FIELDS.forEach((field) => {
        if (this.value[field] !== undefined) {
          this[field] = this.value[field];
        }
      });

      if (!skipSync) {
        this.sameOutputLanguage = !this.outputLanguageId;
        this.disabledOutputLanguages =
          this.inputLanguageId != null ? [this.inputLanguageId] : [];
      }
    },

    getGradingDetails() {
      return FIELDS.reduce(
        (acc, field) => Object.assign(acc, { [field]: this[field] }),
        {}
      );
    },

    handleLevelChange(level) {
      this.updateFields({
        level,
        length:
          FEEDBACK_LENGTH[MAP_GRADE_LEVELS_TO_FEEDBACK_LENGTH_INDEX[level]]
            .value,
      });
    },

    updateField(field, newValue) {
      this.updateFields({ [field]: newValue });
    },

    updateFields(fields) {
      const newGradingDetails = { ...this.getGradingDetails(), ...fields };

      Object.keys(newGradingDetails).forEach((field) => {
        this[field] =
          field in fields ? fields[field] : newGradingDetails[field];
      });

      this._selfUpdate = true;
      this.$emit("input", newGradingDetails);
    },

    setRubric(rubric) {
      const currentGradingDetails = this.getGradingDetails();
      const updatedFields = {};

      if (rubric?.gradeLevel && this.value.level == null) {
        updatedFields.level = rubric.gradeLevel;
        updatedFields.length =
          FEEDBACK_LENGTH[
            MAP_GRADE_LEVELS_TO_FEEDBACK_LENGTH_INDEX[rubric.gradeLevel]
          ].value;
      }

      if (rubric?.intensity && this.value.intensity == null) {
        updatedFields.intensity = rubric.intensity;
      }

      if (rubric?.language && this.value.inputLanguageId == null) {
        updatedFields.inputLanguageId = rubric.language?.id;
      }

      if (rubric?.id && currentGradingDetails.rubricId !== rubric.id) {
        updatedFields.rubricId = rubric.id;
      }

      if (currentGradingDetails.flexibleScoring !== rubric?.flexibleScoring) {
        updatedFields.flexibleScoring = rubric?.flexibleScoring ?? false;
      }

      if (Object.keys(updatedFields).length > 0) {
        this.updateFields(updatedFields);
      }
    },

    onInputLanguageChange(languageId) {
      this.disabledOutputLanguages = languageId != null ? [languageId] : [];
      if (
        !this.sameOutputLanguage &&
        languageId != null &&
        languageId === this.outputLanguageId
      ) {
        this.sameOutputLanguage = true;
        this.previousOutputLanguageId = this.outputLanguageId;
        this.updateFields({
          inputLanguageId: languageId,
          outputLanguageId: null,
        });
      } else {
        this.updateField("inputLanguageId", languageId);
      }
    },

    onSameLanguageToggle(checked) {
      this.sameOutputLanguage = checked;
      this.toggleSameOutputLanguage(checked);
    },

    toggleSameOutputLanguage(checked) {
      if (checked) {
        this.previousOutputLanguageId = this.outputLanguageId;
        this.updateField("outputLanguageId", null);
      } else {
        this.disabledOutputLanguages =
          this.inputLanguageId != null ? [this.inputLanguageId] : [];
        const restoreId = this.previousOutputLanguageId;
        if (restoreId && restoreId !== this.inputLanguageId) {
          this.updateField("outputLanguageId", restoreId);
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .additional-collapse {
    padding: 0;
    border: none;
    margin-bottom: 1rem;
  }
  .additional-collapse-control {
    width: max-content !important;
  }
}
</style>
